/* global snaptr */

import { useState, useEffect } from "react";
import { getAnalytics, logEvent } from "firebase/analytics";
import { app } from "../../firebase-config";
import LeadCaptureForm from "../LeadCaptureForm";

import {
  Plus,
  Minus,
  CheckCircle2,
  Tag,
  ShoppingCart,
  Contact2,
  Users2,
  Users,
  Sparkles,
  ArrowRight,
  Package,
  Smartphone,
  Zap,
  Shield,
  Clock,
  Brain,
  Eye,
  Target,
  Heart,
  FlaskConical,
  Lightbulb,
  Rocket,
  ChevronDown,
  ChevronUp,
  Lock,
  Mail
} from "lucide-react";
import { GooglePlayButton, AppStoreButton } from "react-mobile-app-button";
import { Link } from "react-router-dom";

import styles from "./Application.module.css";
import reserveStyles from "./Reserve.module.css";
import ImageGallery from "../ImageGallery";

const Reserve = ({ cartItems, setCartItems }) => {
  const [loading, setLoading] = useState(false);
  const [activeStage, setActiveStage] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);

  const reserveFaqs = [
    {
      q: "When will my sensor ship?",
      a: "We're targeting Q4 2026 for initial shipments. Founding members will receive priority shipping in the order reservations were placed."
    },
    {
      q: "Can I get a refund if I change my mind?",
      a: "Absolutely. Your reservation is fully refundable at any time until your sensor ships. No questions asked."
    },
    {
      q: "What if I don't like it after I receive it?",
      a: "We offer a 30-day satisfaction guarantee after delivery. If Qwytt isn't right for you, return it for a full refund."
    },
    {
      q: "Is my payment secure?",
      a: "Yes. All payments are processed through Stripe, the same payment processor used by Amazon, Google, and millions of businesses worldwide. We never store your card details."
    }
  ];
  const iosUrl = "https://apps.apple.com/us/app/qwytt-nicotine/id6657960125";
  const androidUrl = "https://play.google.com/store/apps/details?id=com.qwytt.nicotine.prod";
  
  // Firebase Analytics
  const analytics = getAnalytics(app);

  // Auto-cycle through stages
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStage((prev) => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Track page view
  useEffect(() => {
    logEvent(analytics, "page_view", {
      page_title: "Reserve Sensor",
      page_location: window.location.href,
      page_path: "/reserve"
    });
  }, [analytics]);

  function getSnapClickId() {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get("sc_click_id") || undefined;
    } catch {
      return undefined;
    }
  }

  function addItem(productId, price) {
    setCartItems((prev) => {
      const newQuantity = (prev[productId]?.quantity ?? 0) + 1;

      // Firebase Analytics - Add to cart
      logEvent(analytics, "add_to_cart", {
        currency: "USD",
        value: price / 100,
        items: [{
          item_id: productId,
          item_name: `Qwytt ${productId} Pack`,
          price: price / 100,
          quantity: newQuantity
        }]
      });

      if (window.snaptr) {
        snaptr("track", "ADD_CART", {
          price: price / 100,
          currency: "USD",
          item_ids: [productId],
          number_items: newQuantity,
          uuid_c1: getSnapClickId(),
        });
      }

      return {
        ...prev,
        [productId]: {
          quantity: newQuantity,
          price,
        },
      };
    });
  }

  function removeItem(productId) {
    setCartItems((prev) => {
      if (!prev[productId]) return prev;

      const updated = { ...prev };
      const current = prev[productId]?.quantity ?? 0;

      // Firebase Analytics - Remove from cart
      logEvent(analytics, "remove_from_cart", {
        currency: "USD",
        value: prev[productId].price / 100,
        items: [{
          item_id: productId,
          item_name: `Qwytt ${productId} Pack`,
          price: prev[productId].price / 100,
          quantity: 1
        }]
      });

      if (current > 0 && window.snaptr) {
        snaptr("track", "REMOVE_CART", {
          item_ids: [productId],
          number_items: current - 1,
          uuid_c1: getSnapClickId(),
        });
      }

      if (current <= 1) {
        delete updated[productId];
      } else {
        updated[productId] = {
          ...prev[productId],
          quantity: current - 1,
        };
      }

      return updated;
    });
  }

  const handleAppStoreClick = (platform) => {
    logEvent(analytics, "app_store_click", {
      platform: platform,
      page: "reserve",
      location: "secondary_cta"
    });
    
    if (window.snaptr) {
      snaptr("track", "APP_OPEN", {
        destination: `app_store_${platform}`,
        uuid_c1: getSnapClickId(),
      });
    }
  };

  const handleCheckoutClick = () => {
    const totalItems = Object.values(cartItems).reduce(
      (sum, item) => sum + item.quantity,
      0
    );
    const totalValue = Object.values(cartItems).reduce(
      (sum, item) => sum + (item.price * item.quantity),
      0
    );

    logEvent(analytics, "begin_checkout", {
      currency: "USD",
      value: totalValue / 100,
      items: Object.entries(cartItems).map(([id, item]) => ({
        item_id: id,
        item_name: `Qwytt ${id} Pack`,
        price: item.price / 100,
        quantity: item.quantity
      }))
    });
  };

  const progressStages = [
    {
      id: "patent",
      label: "Design",
      icon: <Lightbulb size={18} />,
      image: "/images/patent1.png",
      caption: "Patent-pending technology"
    },
    {
      id: "prototype",
      label: "Prototyping",
      icon: <FlaskConical size={18} />,
      image: "/images/demo.gif",
      caption: "Working prototype with BLE"
    },
    {
      id: "product",
      label: "Coming Soon",
      icon: <Rocket size={18} />,
      image: "/images/person3-min.jpg",
      caption: "Real people, real results"
    }
  ];

  const itemOptions = [
    {
      productName: "Partner Pack",
      productId: "double",
      icon: <Users2 size={28} />,
      price: 4999 * 2 * 0.9,
      discountedFrom: 4999 * 2,
      popular: true,
      bullets: [
        "Two early access edition sensors for you and a partner",
        "Quit together with shared accountability",
        "10% off release device pricing for you and your partner",
      ],
    },
    {
      productName: "Single",
      productId: "single",
      icon: <Contact2 size={28} />,
      price: 4999,
      discountedFrom: null,
      popular: false,
      bullets: [
        "One rechargeable, early access edition Qwytt sensor",
        "Automatic puff tracking for your vape",
      ],
    },
    {
      productName: "Group Pack",
      productId: "four",
      icon: <Users size={28} />,
      price: 4999 * 4 * 0.85,
      discountedFrom: 4999 * 4,
      popular: false,
      bullets: [
        "Four early access edition sensors for your group",
        "15% off release device pricing for your group",
        "1 month premium features for your group",
      ],
    },
  ];

  function formatPrice(amount) {
    return (amount / 100).toFixed(2);
  }

  const totalItems = Object.values(cartItems).reduce(
    (sum, item) => sum + item.quantity,
    0
  );

  return (
    <div className={styles.page}>
      <div className={reserveStyles.container}>
        {/* ========== HERO ========== */}
        <div className={reserveStyles.hero}>
          <div className={reserveStyles.badge}>
            <Sparkles size={16} />
            <span>Founding Member Access</span>
          </div>

          <h1 className={reserveStyles.title}>
            Quit on Autopilot
          </h1>

          <p className={reserveStyles.subtitle}>
            The quit coach that tracks every puff so you don't have to.
            Get early access and help shape the future of quit-tech.
          </p>
        </div>

        {/* ========== PRODUCT SHOWCASE ========== */}
        <div className={reserveStyles.productShowcase}>
          <div className={reserveStyles.imageWrapper}>
            <div className={reserveStyles.imageGlow}></div>
            <img
              className={reserveStyles.productImage}
              src="images/cuitt_45.png"
              alt="Qwytt sensor device"
            />
          </div>
        </div>
{/* ========== PROGRESS SHOWCASE ========== */}
{/* Replace the existing progressSection with this */}

<section className={reserveStyles.progressSection}>
  <h2>From Concept to Reality</h2>
  <p className={reserveStyles.progressIntro}>
    We're not just an idea—we're building something real.
  </p>

  <div className={reserveStyles.progressShowcase}>
    {/* Progress Indicator Dots */}
    <div className={reserveStyles.progressIndicators}>
      <button
        className={`${reserveStyles.progressDot} ${activeStage === 0 ? reserveStyles.progressDotActive : ""} ${activeStage > 0 ? reserveStyles.progressDotComplete : ""}`}
        onClick={() => setActiveStage(0)}
      >
        <Lightbulb size={18} />
        <span>Research</span>
      </button>
      <button
        className={`${reserveStyles.progressDot} ${activeStage === 1 ? reserveStyles.progressDotActive : ""} ${activeStage > 1 ? reserveStyles.progressDotComplete : ""}`}
        onClick={() => setActiveStage(1)}
      >
        <FlaskConical size={18} />
        <span>Prototyping</span>
      </button>
      <button
        className={`${reserveStyles.progressDot} ${activeStage === 2 ? reserveStyles.progressDotActive : ""}`}
        onClick={() => setActiveStage(2)}
      >
        <Rocket size={18} />
        <span>Coming Soon</span>
      </button>
      {/* Connecting line */}
      <div className={reserveStyles.progressLine}>
        <div 
          className={reserveStyles.progressLineFill}
          style={{ width: `${(activeStage / 2) * 100}%` }}
        ></div>
      </div>
    </div>

    {/* Fixed Height Container */}
    <div className={reserveStyles.progressStageContainer}>
      
      {/* Stage 1: Patents */}
      <div className={`${reserveStyles.progressStage} ${activeStage === 0 ? reserveStyles.progressStageActive : ""}`}>
        <div className={reserveStyles.patentGrid}>
          <img src="/images/patent1.png" alt="Patent diagram showing sensor design" />
          <img src="/images/patent2.png" alt="Patent diagram showing system architecture" />
        </div>
        <div className={reserveStyles.progressCaption}>
          <span className={reserveStyles.progressStageNumber}>01</span>
          <span className={reserveStyles.progressCaptionText}>Patent-pending technology</span>
        </div>
      </div>

      {/* Stage 2: Prototypes */}
      <div className={`${reserveStyles.progressStage} ${activeStage === 1 ? reserveStyles.progressStageActive : ""}`}>
        <div className={reserveStyles.prototypeLayout}>
          <div className={reserveStyles.prototypeMain}>
            <img src="/images/demo.gif" alt="Working prototype demo" />
          </div>
          <div className={reserveStyles.prototypeGrid}>
            <img src="/images/prototype.jpg" alt="Prototype v2" />
            <img src="/images/prototype3.jpg" alt="Prototype v3" />
          </div>
        </div>
        <div className={reserveStyles.progressCaption}>
          <span className={reserveStyles.progressStageNumber}>02</span>
          <span className={reserveStyles.progressCaptionText}>Working prototypes with BLE</span>
        </div>
      </div>

      {/* Stage 3: Final Product */}
      <div className={`${reserveStyles.progressStage} ${activeStage === 2 ? reserveStyles.progressStageActive : ""}`}>
        <div className={reserveStyles.personImageWrapper}>
          <img 
            src="/images/person3-min.jpg" 
            alt="Person using Qwytt sensor" 
            className={reserveStyles.personImage}
          />
        </div>
        <div className={reserveStyles.progressCaption}>
          <span className={reserveStyles.progressStageNumber}>03</span>
          <span className={reserveStyles.progressCaptionText}>Real people, real results — Q4 2026</span>
        </div>
      </div>

    </div>

    {/* Mobile Thumbnails */}
    <div className={reserveStyles.progressThumbnails}>
      <button
        className={`${reserveStyles.progressThumb} ${activeStage === 0 ? reserveStyles.progressThumbActive : ""}`}
        onClick={() => setActiveStage(0)}
      >
        <img src="/images/patent1.png" alt="Patent" />
      </button>
      <button
        className={`${reserveStyles.progressThumb} ${activeStage === 1 ? reserveStyles.progressThumbActive : ""}`}
        onClick={() => setActiveStage(1)}
      >
        <img src="/images/demo.gif" alt="Prototype" />
      </button>
      <button
        className={`${reserveStyles.progressThumb} ${activeStage === 2 ? reserveStyles.progressThumbActive : ""}`}
        onClick={() => setActiveStage(2)}
      >
        <img src="/images/person3-min.jpg" alt="Product" />
      </button>
    </div>
  </div>
</section>

        {/* ========== WHAT MAKES IT DIFFERENT ========== */}
        <section className={reserveStyles.differentiatorSection}>
          <h2>The First Accessory Sensor for E-cigs</h2>
          <p className={reserveStyles.differentiatorIntro}>
            Other quit tools rely on self-reporting or require you to switch to a new device. 
            Qwytt is different.
          </p>

          <div className={reserveStyles.differentiatorGrid}>
            <div className={reserveStyles.differentiatorCard}>
              <Eye size={28} className={reserveStyles.diffIcon} />
              <h3>Passive Monitoring</h3>
              <p>
                Your vape becomes your guide. Same device you're already using, now with a witness tracking every puff and keeping you accountable.
              </p>
            </div>

            <div className={reserveStyles.differentiatorCard}>
              <Brain size={28} className={reserveStyles.diffIcon} />
              <h3>Personalized Goals</h3>
              <p>
                After 7 days, our behavioral engine creates reduction targets based on 
                YOUR patterns, not generic advice.
              </p>
            </div>

            <div className={reserveStyles.differentiatorCard}>
              <Zap size={28} className={reserveStyles.diffIcon} />
              <h3>Instant Feedback</h3>
              <p>
                LED cues on the sensor + instant app notifications. Know where you 
                stand at every moment.
              </p>
            </div>

            <div className={reserveStyles.differentiatorCard}>
              <Shield size={28} className={reserveStyles.diffIcon} />
              <h3>Works for Everyone</h3>
              <p>
                Because it's an accessory and does not contain or facilitate nicotine use, Qwytt is accessible 
                to all ages.
              </p>
            </div>
          </div>
        </section>

        {/* ========== DEVICE SPECS ========== */}
        <div className={styles.deviceText}>
          <h2>The Qwytt Sensor</h2>
          <p>
            Our sensor uses pressure, motion, and environmental sensing 
            to detect vaping use with precision, all in a friendly 
            package that lasts months on a single charge.
          </p>
          <ul>
            <li>Clips onto most disposable and pod-based vapes</li>
            <li>Battery life: up to 2 months</li>
            <li>BLE connection with instant sync</li>
            <li>On-device LED feedback</li>
            <li>Compact, discreet design</li>
          </ul>
          <p id={reserveStyles.disclaimer}>
              Images depicted are renderings, final design and specifications may evolve based on user feedback
          </p>
        </div>

        {/* ========== WHY RESERVE NOW ========== */}
        <section className={reserveStyles.whyReserveSection}>
          <h2>Why Reserve Now?</h2>
          <div className={reserveStyles.whyReserveGrid}>
            <div className={reserveStyles.whyReserveCard}>
              <Tag size={24} className={reserveStyles.whyIcon} />
              <h3>Founding Member Pricing</h3>
              <p>Lock in early-access pricing before we launch publicly at full retail.</p>
            </div>
            <div className={reserveStyles.whyReserveCard}>
              <Heart size={24} className={reserveStyles.whyIcon} />
              <h3>Shape the Product</h3>
              <p>Founding members get direct input into features and development priorities.</p>
            </div>
            <div className={reserveStyles.whyReserveCard}>
              <Shield size={24} className={reserveStyles.whyIcon} />
              <h3>Zero Risk</h3>
              <p>Fully refundable until shipment. Change your mind? No problem.</p>
            </div>
          </div>
        </section>

        {/* ========== URGENCY BANNER ========== */}
        <div className={reserveStyles.urgencyBanner}>
          <Sparkles size={18} />
          <span>
            <strong>Founding Member Pricing</strong> – Price increases to $69.99 after first 1,000 units ship
          </span>
        </div>

        {/* ========== PRICING CARDS ========== */}
        <div className={reserveStyles.pricingSection}>
          <h2 className={reserveStyles.sectionTitle}>Choose Your Pack</h2>

          <div className={reserveStyles.itemsGrid}>
            {itemOptions.map((item) => {
              const quantity = cartItems[item.productId]?.quantity ?? 0;
              const isSelected = quantity > 0;

              return (
                <div
                  key={item.productId}
                  className={`${reserveStyles.itemCard} ${
                    item.popular ? reserveStyles.popularCard : ""
                  } ${isSelected ? reserveStyles.selectedCard : ""}`}
                >
                  {item.popular && (
                    <div className={reserveStyles.popularBadge}>
                      <Sparkles size={14} />
                      Most Popular
                    </div>
                  )}

                  <div className={reserveStyles.cardHeader}>
                    <div className={reserveStyles.iconWrapper}>{item.icon}</div>
                    <h3 className={reserveStyles.productName}>{item.productName}</h3>
                  </div>

                  <div className={reserveStyles.priceSection}>
                    <div className={reserveStyles.priceMain}>
                      <span className={reserveStyles.currency}>$</span>
                      <span className={reserveStyles.amount}>
                        {formatPrice(item.price).split(".")[0]}
                      </span>
                      <span className={reserveStyles.cents}>
                        .{formatPrice(item.price).split(".")[1]}
                      </span>
                    </div>

                    {item.discountedFrom && (
                      <div className={reserveStyles.discountBadge}>
                        <Tag size={12} />
                        <span className={reserveStyles.originalPrice}>
                          ${formatPrice(item.discountedFrom)}
                        </span>
                        <span className={reserveStyles.savings}>
                          Save ${formatPrice(item.discountedFrom - item.price)}
                        </span>
                      </div>
                    )}
                  </div>

                  <ul className={reserveStyles.featureList}>
                    {item.bullets.map((bullet, i) => (
                      <li key={i} className={reserveStyles.featureItem}>
                        <CheckCircle2 size={18} className={reserveStyles.checkIcon} />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>

                  <div className={reserveStyles.cardFooter}>
                    <div className={reserveStyles.quantityControl}>
                      <button
                        aria-label={`Remove ${item.productId}`}
                        onClick={() => removeItem(item.productId)}
                        className={reserveStyles.quantityBtn}
                        disabled={quantity === 0}
                      >
                        <Minus size={18} />
                      </button>

                      <div className={reserveStyles.quantityDisplay}>{quantity}</div>

                      <button
                        aria-label={`Add ${item.productId}`}
                        onClick={() => addItem(item.productId, item.price)}
                        className={reserveStyles.quantityBtn}
                      >
                        <Plus size={18} />
                      </button>
                    </div>

                    {isSelected && (
                      <div className={reserveStyles.selectedIndicator}>
                        <CheckCircle2 size={16} />
                        Added to cart
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ========== CHECKOUT BUTTON ========== */}
        {totalItems > 0 && (
          <div className={reserveStyles.checkoutSection}>
            <div className={reserveStyles.cartSummary}>
              <Package size={20} />
              <span>
                {totalItems} {totalItems === 1 ? "item" : "items"} in cart
              </span>
            </div>

            <Link 
              to="/checkout" 
              className={reserveStyles.checkoutLink}
              onClick={handleCheckoutClick}
            >
              <button className={reserveStyles.checkoutButton} disabled={loading}>
                <span>Proceed to Checkout</span>
                <ArrowRight size={20} />
              </button>
            </Link>
          </div>
        )}

        {/* ========== TRUST INDICATORS ========== */}
        <div className={reserveStyles.trustSection}>
          <div className={reserveStyles.trustCard}>
            <Shield size={24} />
            <div>
              <strong>30-Day Money Back Guarantee</strong>
              <span>Not satisfied? Full refund, no questions asked.</span>
            </div>
          </div>
          <div className={reserveStyles.trustCard}>
            <Clock size={24} />
            <div>
              <strong>Expected Ship: Q4 2026</strong>
              <span>Founding members ship first.</span>
            </div>
          </div>
          <div className={reserveStyles.trustCard}>
            <Lock size={24} />
            <div>
              <strong>Secure Checkout</strong>
              <span>256-bit SSL encryption via Stripe</span>
            </div>
          </div>
          <div className={reserveStyles.trustCard}>
            <Mail size={24} />
            <div>
              <strong>Questions? We're Here</strong>
              <span>support@qwytt.ai</span>
            </div>
          </div>
        </div>

        {/* ========== FAQ ========== */}
        <section className={reserveStyles.faqSection}>
          <h2>Common Questions</h2>
          <div className={reserveStyles.faqList}>
            {reserveFaqs.map((faq, index) => (
              <div
                key={index}
                className={`${reserveStyles.faqItem} ${openFaq === index ? reserveStyles.faqOpen : ''}`}
              >
                <button
                  className={reserveStyles.faqQuestion}
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span>{faq.q}</span>
                  {openFaq === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {openFaq === index && (
                  <div className={reserveStyles.faqAnswer}>
                    <p>{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* ========== IMMEDIATE VALUE ========== */}
        {/* <section className={reserveStyles.immediateValue}>
          <div className={reserveStyles.immediateValueContent}>
            <div className={reserveStyles.immediateValueText}>
              <h2>Don't Wait to Start Quitting</h2>
              <p>
                Claim your sensor now, then download the free app <strong>today</strong>. 
                Start tracking manually and build your baseline.  When your sensor arrives, 
                it syncs seamlessly with your existing data.
              </p>
            </div>

            <div className={reserveStyles.journeyTimeline}>
              <div className={reserveStyles.journeyStep}>
                <div className={reserveStyles.journeyIcon}>
                  <Smartphone size={20} />
                </div>
                <div className={reserveStyles.journeyContent}>
                  <strong>Today</strong>
                  <span>Download app, start tracking</span>
                </div>
              </div>
              <div className={reserveStyles.journeyConnector}></div>
              <div className={reserveStyles.journeyStep}>
                <div className={reserveStyles.journeyIcon}>
                  <Target size={20} />
                </div>
                <div className={reserveStyles.journeyContent}>
                  <strong>Week 1</strong>
                  <span>Establish your baseline</span>
                </div>
              </div>
              <div className={reserveStyles.journeyConnector}></div>
              <div className={reserveStyles.journeyStep}>
                <div className={reserveStyles.journeyIcon}>
                  <Brain size={20} />
                </div>
                <div className={reserveStyles.journeyContent}>
                  <strong>Week 2+</strong>
                  <span>Get personalized goals</span>
                </div>
              </div>
              <div className={reserveStyles.journeyConnector}></div>
              <div className={reserveStyles.journeyStep}>
                <div className={reserveStyles.journeyIcon}>
                  <Package size={20} />
                </div>
                <div className={reserveStyles.journeyContent}>
                  <strong>Q4 2026</strong>
                  <span>Sensor arrives, goes automatic</span>
                </div>
              </div>
            </div>
          </div>
        </section> */}

        <ImageGallery theme="dark" />

        <section className={reserveStyles.emailCaptureSection}>
          <LeadCaptureForm
            pageName="reserve"
            landingPage="/reserve"
            title="Not Ready to Reserve?"
            subtitle="Get updates on launch dates, early-bird deals, and sensor development."
            buttonText="Keep Me Updated"
            showPerks={false}
          />
        </section>

        {/* ========== START NOW CTA ========== */}
        <section
          className={styles.secondaryCta}
          style={{ marginTop: "2em", borderRadius: "20px" }}
        >
          <h2>Start Today</h2>
          <p>
            Download the app now and begin tracking. Your journey doesn't start 
            when the sensor arrives, it begins today.
          </p>
          <div onClick={() => handleAppStoreClick("android")}>
            <GooglePlayButton url={androidUrl} theme="dark" />
          </div>

          <div onClick={() => handleAppStoreClick("ios")}>
            <AppStoreButton url={iosUrl} theme="dark" />
          </div>
        </section>
      </div>
    </div>
  );
};

export default Reserve;